"use client";
import type {
  AuditFormProps,
  SessionProgress,
  SetSessionProgress,
} from "../../../types/audit-form";

import type { AuditFileMetadata } from "../../../types/audit-file";
import { useLatest } from "../../../hooks/use-latest";
import { useImageDescriptions } from "../../../hooks/use-image-descriptions";
import {
  errorMessage,
  requireMutationSuccess,
} from "../../../utils/mutationResult.js";

import {
  AlertCircle,
  Building,
  Camera,
  Eye,
  Home,
  Loader2,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDebouncedSave } from "../../../hooks/use-debounced-save";
import { useDraftAutosave } from "../../../hooks/use-draft-autosave";
import { useStableMemoComponent } from "../../../hooks/use-stable-memo-component";
import {
  useVersionAwareFiles,
  useVersionAwareSiteData,
} from "../../../hooks/use-version-aware-data";
import { useNotification } from "../../../hooks/useNotification";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  deleteFile,
  fetchFiles,
  updateFile,
  uploadMultipleFiles,
} from "../../../redux/slices/fileSlice.js";
import { updateSiteData } from "../../../redux/slices/siteDataSlice.js";
import { notificationHelpers } from "../../../utils/notificationHelpers";
import { OfflineImage } from "../../shared/offline-image";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card.jsx";
import { LoadingIndicator } from "../../ui/loading-indicator";
import {
  OptimizedCheckbox,
  OptimizedCheckboxGroup,
  OptimizedInput,
  OptimizedSelect,
  OptimizedTextarea,
} from "../optimized-form-field";
import { ImageCapturePicker } from "./image-capture-picker";
import { LocalSectionImportControl } from "./local-section-import-control";

interface PlanningAspectsFormProps extends AuditFormProps {
  siteId: string;
  templateId?: string;
  reportId?: string;
  sessionProgress: SessionProgress;
  setSessionProgress: SetSessionProgress;
  previousCompletedReports?: any[];
}

interface PlanningImage extends AuditFileMetadata {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  description: string;
  uploadDate: Date;
}

interface PlanningAspectsData {
  planningAspectsRemarks?: string;
  // Boolean fields
  visibilityFromDwellings: boolean;
  visibilityFromCommercial: boolean;
  authorityConsultationCompleted: boolean;

  // Text fields
  authorityFeedback: string;
  utilityCompanyConditions: string;
  utilityCompanyRequirements: string;
  substationDeliveryBy: string;

  // Multi-select fields
  siteDesignations: string[];
  otherDesignation: string;

  // Images
  planningAspectsImages: PlanningImage[];
}

const initialData: PlanningAspectsData = {
  visibilityFromDwellings: false,
  visibilityFromCommercial: false,
  authorityConsultationCompleted: false,
  authorityFeedback: "",
  utilityCompanyConditions: "",
  utilityCompanyRequirements: "",
  substationDeliveryBy: "",
  siteDesignations: [],
  otherDesignation: "",
  planningAspectsImages: [],
};

const siteDesignationOptions = [
  { value: "protected-landscape", label: "Protected landscape" },
  {
    value: "ecologically-sensitive-zone",
    label: "Ecologically sensitive zone",
  },
  { value: "flood-management-zone", label: "Flood management zone" },
  {
    value: "high-value-agricultural-land",
    label: "High-value agricultural land",
  },
  { value: "contaminated-land", label: "Contaminated land" },
  { value: "unexploded-ordnance", label: "Unexploded ordnance" },
  { value: "none", label: "None of the above" },
  { value: "other", label: "Other (specify)" },
];

const substationDeliveryOptions = [
  { value: "project-developer", label: "Project Developer" },
  { value: "utility-company", label: "Utility Company" },
  { value: "third-party-contractor", label: "Third Party Contractor" },
  { value: "joint-delivery", label: "Joint Delivery (Developer & Utility)" },
  { value: "not-determined", label: "Not yet determined" },
  { value: "other", label: "Other (specify)" },
];

const progressFields = ["siteDesignations"];

const calculateProgress = (formData: PlanningAspectsData = initialData) => {
  const fields = progressFields;
  const filledCount = fields.filter((field) => {
    const value = formData[field as keyof PlanningAspectsData];

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return typeof value === "string"
      ? value.trim() !== ""
      : Array.isArray(value)
        ? value.length > 0
        : value !== null && value !== undefined;
  }).length;

  return Math.round((filledCount / fields.length) * 100);
};

export const PlanningAspectsForm = memo(function PlanningAspectsForm({
  siteId,
  templateId,
  reportId,
  sessionProgress,
  setSessionProgress,
  readOnly = false,
  previousCompletedReports = [],
}: PlanningAspectsFormProps) {
  const { notify } = useNotification();

  const { loading } = useAppSelector((state) => state?.files);
  const [formData, setFormData] = useState<PlanningAspectsData>(initialData);
  const formDataRef = useLatest(formData);
  const [selectedImage, setSelectedImage] = useState<PlanningImage | null>(
    null,
  );
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [uploadingImages, setUploadingImages] = useState<{
    [key: string]: boolean;
  }>({});

  const dispatch = useAppDispatch();

  // Fetch existing files
  useEffect(() => {
    dispatch(
      fetchFiles({
        siteId,
        reportId,
      }),
    );
  }, [dispatch, siteId, reportId]);

  // Load current site data for the selected report
  const currentSiteData = useVersionAwareSiteData(readOnly);
  const files = useVersionAwareFiles(readOnly);
  const [imageDescriptions, setImageDescriptions] = useImageDescriptions(files);

  // Autosave unsaved edits to IndexedDB so nothing is lost if the app closes
  // before the debounced server save fires (e.g. offline, tab closed, phone
  // loses power). Mirrors the pattern in site-overview.tsx / site-data.tsx.
  const draftKey =
    !readOnly && reportId ? `report:${reportId}:planning-aspects` : null;
  const { restoreDraft, clearSavedDraft } = useDraftAutosave(
    draftKey,
    formData,
  );
  const draftRestoredRef = useRef(false);

  // Restore any unsaved draft once on mount, before server data can
  // overwrite it below. Only applies if a draft exists, so a freshly-fetched
  // server record from a different session is never clobbered unexpectedly.
  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    restoreDraft().then((draft) => {
      if (draft?.data) {
        setFormData((prev) => ({ ...prev, ...draft.data }));
      }
    });
  }, [draftKey, restoreDraft]);

  // Once the local form state matches what's confirmed saved on the server,
  // the IndexedDB draft is redundant — clear it so a future mount doesn't
  // restore stale local data over a fresher server record.
  useEffect(() => {
    if (!draftKey || !currentSiteData) return;
    if (JSON.stringify(currentSiteData) === JSON.stringify(formData)) {
      clearSavedDraft();
    }
  }, [draftKey, currentSiteData, formData, clearSavedDraft]);

  useEffect(() => {
    // Update session progress
    const prog = calculateProgress(formData);
    setSessionProgress((prev) => ({
      ...prev,
      [templateId]: { ...prev[templateId], "planning-aspects": prog },
    }));
  }, [formData, setSessionProgress, templateId]);

  // Save handler
  const handleSave = useCallback(async () => {
    try {
      const mutationResult1 = await requireMutationSuccess(
        dispatch(
          updateSiteData({
            siteDataId: siteId,
            siteDataInfo: { planningAspectsData: formData },
          }),
        ),
      );
      const config = notificationHelpers.formSaveSuccess("Planning Aspects", [
        mutationResult1,
      ]);
      notify(config.message, config.type, config.options);
    } catch (error) {
      console.error("Save failed:", error);
      const config = notificationHelpers.formSaveError(
        "Planning Aspects",
        errorMessage(error),
      );
      notify(config.message, config.type, config.options);
      throw error;
    }
  }, [siteId, formData, dispatch, notify]);

  // Debounced save
  const {
    isSaving: isFormSaving,
    lastSaved: lastSavedForm,
    hasUnsavedChanges: hasFormUnsavedChanges,
    saveError: formSaveError,
    forceSave: forceFormSave,
    resetBaseline: resetFormBaseline,
  } = useDebouncedSave(formData, {
    delay: 7500,
    onSave: handleSave,
    onError: (error) => console.error("Save error:", error),
    readOnly,
  });

  useEffect(() => {
    if (currentSiteData) {
      const newFormData = {
        ...formDataRef.current,
        ...(currentSiteData?.planningAspectsData || {}),
      };
      resetFormBaseline(newFormData);
      setFormData(newFormData);
    }
  }, [currentSiteData, resetFormBaseline, formDataRef]);

  // Save handler for image descriptions
  const handleSaveImages = useCallback(
    async (descriptions: Record<string, string>) => {
      try {
        const mutationResult2 = await Promise.all(
          Object.entries(descriptions).map(([fileId, fileDescription]) =>
            requireMutationSuccess(
              dispatch(updateFile({ fileId, fileData: { fileDescription } })),
            ),
          ),
        );
        const config = notificationHelpers.formSaveSuccess(
          "Image Descriptions",
          [mutationResult2],
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Image Descriptions",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
        throw error;
      }
    },
    [dispatch, notify],
  );

  // Debounced save for image descriptions (5000ms delay)
  const {
    isSaving: isImageSaving,
    lastSaved: lastSavedImage,
    hasUnsavedChanges: hasImageUnsavedChanges,
    saveError: imageSaveError,
    forceSave: forceImageSave,
  } = useDebouncedSave(imageDescriptions, {
    delay: 5000,
    onSave: handleSaveImages,
    onError: (error) => console.error("Image description save error:", error),
    readOnly,
  });

  // Combine form and image save states
  const isSaving = isFormSaving || isImageSaving;
  const hasUnsavedChanges = hasFormUnsavedChanges || hasImageUnsavedChanges;
  const saveError = formSaveError || imageSaveError;
  const lastSaved = useMemo(() => {
    if (!lastSavedForm) return lastSavedImage;
    if (!lastSavedImage) return lastSavedForm;
    return lastSavedForm > lastSavedImage ? lastSavedForm : lastSavedImage;
  }, [lastSavedForm, lastSavedImage]);

  const forceSave = useCallback(() => {
    forceFormSave();
    forceImageSave();
  }, [forceFormSave, forceImageSave]);

  // Field change handler
  const handleFieldChange = useCallback((field: keyof PlanningAspectsData) => {
    return (value: string | boolean | string[]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    };
  }, []);

  // Handle site designation selection with special logic for "other"
  const handleSiteDesignationChange = useCallback(
    (selectedDesignations: string[]) => {
      setFormData((prev) => {
        let newDesignations = [...selectedDesignations];

        // If "other" is selected and wasn't before, ensure we include it
        if (
          selectedDesignations.includes("other") &&
          !prev.siteDesignations.includes("other")
        ) {
          newDesignations = [...selectedDesignations];
        }
        // If "other" is deselected, clear the other designation text
        else if (
          !selectedDesignations.includes("other") &&
          prev.siteDesignations.includes("other")
        ) {
          newDesignations = selectedDesignations.filter(
            (designation) => designation !== "other",
          );
          // Clear the other designation text
          setFormData((prev) => ({ ...prev, otherDesignation: "" }));
        }

        return { ...prev, siteDesignations: newDesignations };
      });
    },
    [],
  );

  // Handle substation delivery selection with special logic for "other"
  const handleSubstationDeliveryChange = useCallback((value: string) => {
    // If selecting "other", keep the value
    // If deselecting "other" to another value, clear any "other" specification
    if (value === "other") {
      setFormData((prev) => ({ ...prev, substationDeliveryBy: "other" }));
    } else {
      setFormData((prev) => ({
        ...prev,
        substationDeliveryBy: value,
      }));
    }
  }, []);

  // Image upload handler
  const handleImageUpload = useCallback(
    (files: FileList | File[] | null, filterData) => {
      if (!files) return;
      const fileArray = Array.from(files);

      const uploadKey = JSON.stringify(filterData);
      setUploadingImages((prev) => ({ ...prev, [uploadKey]: true }));

      requireMutationSuccess(
        dispatch(
          uploadMultipleFiles({
            files: fileArray,
            metadata: {
              siteId: siteId,
              entityType: `planning-aspects`,
              entityId: reportId,
              reportId,
            },
            filterData,
          }),
        ),
      )
        .then((mutationOutcome) => {
          fileArray.forEach((file) => {
            const config = notificationHelpers.imageUploadSuccess(
              file.name,
              mutationOutcome,
            );
            notify(config.message, config.type, config.options);
          });
        })
        .catch((error) => {
          const config = notificationHelpers.imageUploadError(
            fileArray.length === 1
              ? fileArray[0].name
              : `${fileArray.length} files`,
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })
        .finally(() => {
          setUploadingImages((prev) => ({ ...prev, [uploadKey]: false }));
        });
    },
    [siteId, reportId, dispatch, notify],
  );

  // Update image description — updates local state; debounced save handles the dispatch
  const updateImageDescription = useCallback(
    (imageId: string, description: string) => {
      setImageDescriptions((prev) => ({ ...prev, [imageId]: description }));
    },
    [setImageDescriptions],
  );

  // Remove image handlers
  const handleRemoveImage = useCallback((imageId: string) => {
    setImageToDelete(imageId);
  }, []);

  const confirmRemoveImage = useCallback(() => {
    if (imageToDelete) {
      requireMutationSuccess(dispatch(deleteFile(imageToDelete)))
        .then((mutationOutcome) => {
          const config = notificationHelpers.imageDeleteSuccess(
            "Image",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          const config = notificationHelpers.imageDeleteError(
            "Image",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })
        .finally(() => {
          setImageToDelete(null);
        });
    }
  }, [imageToDelete, dispatch, notify]);

  const cancelRemoveImage = useCallback(() => {
    setImageToDelete(null);
  }, []);

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  // Get loading status
  const getLoadingStatus = () => {
    if (saveError) return "error";
    if (isSaving) return "saving";
    if (hasUnsavedChanges) return "unsaved";
    if (lastSaved) return "saved";
    return "idle";
  };

  // Check if uploading for a section
  const isUploadingForSection = (filterData: any) => {
    const uploadKey = JSON.stringify(filterData);
    return uploadingImages[uploadKey] || false;
  };

  // Image Card Component
  const ImageCard = useStableMemoComponent(
    function ImageCard({
      image,
      onView,
      onRemove,
      onDescriptionUpdate,
      isDeleting,
      imageDescription,
    }: {
      image: PlanningImage;
      onView: (image: PlanningImage) => void;
      onRemove: (id: string) => void;
      onDescriptionUpdate: (id: string, description: string) => void;
      isDeleting: boolean | string;
      imageDescription: string;
    }) {
      return (
        <div key={image.id} className="relative group">
          <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden relative dark:bg-muted">
            <OfflineImage
              fileId={image?.id}
              src={image?.fileUrl}
              alt="Planning aspects image"
              className="w-full h-full object-cover"
            />
            {loading && (isDeleting === true || isDeleting === image.id) && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </div>
            )}
          </div>
          <div className="absolute top-2 right-2 flex gap-1">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
              onClick={() => onView(image)}
              disabled={loading}
              title="View Image"
            >
              <Eye className="w-3 h-3" />
            </Button>
            {!readOnly && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                onClick={() => onRemove(image.id)}
                disabled={loading}
                title="Delete Image"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
          <div className="mt-2 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate truncate">
                {image?.originalName}
              </span>
              <Badge variant="outline" className="text-xs">
                {formatFileSize(image?.fileSize)}
              </Badge>
            </div>
            <OptimizedTextarea
              id={`desc-${image.id}`}
              label=""
              value={imageDescription}
              readOnly={readOnly}
              onChange={(value) => onDescriptionUpdate(image.id, value)}
              placeholder="Describe this planning aspect..."
              rows={2}
              disabled={loading}
            />
            <div className="text-xs text-slate-500 dark:text-muted-foreground">
              Date: {new Date(image.uploadDate).toLocaleDateString()}
            </div>
          </div>
        </div>
      );
    },
    (prevProps, nextProps) => {
      const propsEqual =
        prevProps.image.id === nextProps.image.id &&
        prevProps.imageDescription === nextProps.imageDescription &&
        prevProps.isDeleting === nextProps.isDeleting;
      return propsEqual;
    },
  );

  // Image Section Component
  const ImageSection = useStableMemoComponent(function ImageSection({
    title,
    filterData,
    uploadFilterData,
    emptyMessage,
    buttonText = "Add Images",
  }: {
    title: string;
    filterData: any;
    uploadFilterData: any;
    emptyMessage: string;
    buttonText?: string;
  }) {
    const sectionImages = files?.filter(
      (file) =>
        file?.relatedTo?.entityType === `planning-aspects` &&
        file?.relatedTo?.filterData &&
        JSON.stringify(file?.relatedTo?.filterData) ===
          JSON.stringify(filterData),
    );

    const isUploading = isUploadingForSection(uploadFilterData);

    return (
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">{title}</h4>
          {!readOnly && (
            <ImageCapturePicker
              buttonText={buttonText}
              isUploading={isUploading}
              disabled={loading}
              onConfirm={(selectedFiles) =>
                handleImageUpload(selectedFiles, uploadFilterData)
              }
            />
          )}
        </div>

        {isUploading && (
          <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
            <div className="text-center">
              <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2 dark:text-muted-foreground" />
              <p className="text-sm text-slate-600 dark:text-muted-foreground">Uploading images...</p>
            </div>
          </div>
        )}

        {!isUploading && sectionImages.length === 0 ? (
          <div className="flex items-center justify-between p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
            <div className="flex items-center gap-3">
              <Camera className="w-5 h-5 text-slate-400 dark:text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-foreground">
                  No images added
                </p>
                <p className="text-xs text-slate-500 dark:text-muted-foreground">{emptyMessage}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sectionImages.map((image) => (
              <ImageCard
                key={image.id}
                imageDescription={
                  imageDescriptions[image.id] ?? image?.fileDescription ?? ""
                }
                image={image}
                onView={setSelectedImage}
                onRemove={handleRemoveImage}
                onDescriptionUpdate={updateImageDescription}
                isDeleting={loading ? imageToDelete : false}
              />
            ))}
          </div>
        )}
      </div>
    );
  });

  return (
    <div className="space-y-4 md:space-y-6 relative">
      {!readOnly && (
        <LocalSectionImportControl
          previousCompletedReports={previousCompletedReports}
          sectionName="planning-aspects"
          siteId={siteId}
          reportId={reportId}
          readOnly={readOnly}
          setFormData={setFormData}
        />
      )}

      {/* Floating save section - hidden when viewing read-only version */}
      {!readOnly && (
        <div className="fixed top-25 right-3 z-40 sm:right-4">
          <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-3">
            {/* Status indicator - same size as save button */}
            {false && (
              <div
                className="
          bg-white/95 backdrop-blur-sm
          rounded-full border-2 border-gray-200/80
          shadow-lg
          w-12 h-12 sm:w-16 sm:h-16
          flex items-center justify-center
          group relative
          transition-all duration-300 ease-out
          transform hover:scale-110
          cursor-default
          p-2
        "
              >
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <span className="text-[10px] font-medium text-gray-600 leading-none mb-2 dark:text-muted-foreground">
                    Saved
                  </span>

                  <div className="flex items-center justify-center h-6">
                    <LoadingIndicator
                      status={getLoadingStatus()}
                      lastSaved={lastSaved}
                      error={saveError}
                    />
                  </div>
                </div>

                {/* Tooltip - Fixed with pointer-events-none */}
                <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {getLoadingStatus() === "saving"
                    ? "Saving..."
                    : getLoadingStatus() === "saved"
                      ? `Saved ${lastSaved?.toLocaleTimeString()}`
                      : getLoadingStatus() === "error"
                        ? "Save failed"
                        : "Ready to save"}
                </div>

                {/* Error indicator */}
                {getLoadingStatus() === "error" && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                  </div>
                )}
              </div>
            )}

            {/* Save button - Fixed with pointer-events-none */}
            <Button
              onClick={forceSave}
              disabled={isSaving || readOnly}
              className={`
          relative rounded-full w-12 h-12 sm:w-16 sm:h-16
          transition-all duration-300 ease-out
          flex flex-col items-center justify-center
          backdrop-blur-sm border-2
          shadow-lg hover:shadow-xl
          transform hover:scale-110 active:scale-95
          group
          ${
            hasUnsavedChanges && !readOnly
              ? "bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white border-red-300 dark:bg-gradient-to-br dark:border-red-300 dark:ring-red-300"
              : "bg-white/95 text-gray-600 border-gray-200/80 hover:bg-white/90 dark:bg-muted dark:text-muted-foreground dark:border-border dark:hover:bg-accent"
          }
          ${isSaving || readOnly ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}
        `}
            >
              {isSaving ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-[10px] leading-none">Saving</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <Save className="w-5 h-5 sm:mb-1" />
                  <span className="hidden text-[10px] font-medium leading-none sm:block">
                    Save
                  </span>
                </div>
              )}

              {/* Unsaved changes indicator */}
              {hasUnsavedChanges && !isSaving && (
                <div className="absolute -top-1 -right-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                  <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                </div>
              )}

              {/* Tooltip - Fixed with pointer-events-none */}
              <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                {hasUnsavedChanges ? "Save changes" : "No changes to save"}
              </div>
            </Button>
          </div>
        </div>
      )}

      {/* Planning Aspects Card */}
      <Card>
        <CardHeader>
          <CardTitle>Planning Aspects</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Visibility Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-slate-700 dark:text-foreground">
              Visibility Assessment
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <OptimizedCheckbox
                id="visibilityFromDwellings"
                label={
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4 text-slate-500 dark:text-muted-foreground" />
                    Visible from nearby dwellings
                  </div>
                }
                readOnly={readOnly}
                checked={formData.visibilityFromDwellings}
                onChange={handleFieldChange("visibilityFromDwellings")}
                description="Check if the PV project is visible from nearby residential areas"
              />

              <OptimizedCheckbox
                id="visibilityFromCommercial"
                label={
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-slate-500 dark:text-muted-foreground" />
                    Visible from commercial premises
                  </div>
                }
                readOnly={readOnly}
                checked={formData.visibilityFromCommercial}
                onChange={handleFieldChange("visibilityFromCommercial")}
                description="Check if the PV project is visible from commercial or industrial areas"
              />
            </div>
          </div>

          {/* Authority Consultation Section */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-medium text-slate-700 dark:text-foreground">
              Authority Consultation
            </h3>
            <OptimizedCheckbox
              id="authorityConsultationCompleted"
              label="Initial discussion with local authorities completed"
              checked={formData.authorityConsultationCompleted}
              onChange={handleFieldChange("authorityConsultationCompleted")}
              readOnly={readOnly}
              description="Check if an initial discussion regarding the proposed development has been sought with local authorities"
            />

            {formData.authorityConsultationCompleted && (
              <OptimizedTextarea
                id="authorityFeedback"
                label="Authority feedback / reaction"
                value={formData.authorityFeedback}
                onChange={handleFieldChange("authorityFeedback")}
                placeholder="Describe the reaction and feedback received from local authorities..."
                rows={3}
                readOnly={readOnly}
              />
            )}
          </div>

          {/* Site Designations Section */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-medium text-slate-700 dark:text-foreground">
              Site Designations
            </h3>
            <OptimizedCheckboxGroup
              id="siteDesignations"
              label="Does the site have any of the following designations?"
              values={formData.siteDesignations}
              onChange={handleSiteDesignationChange}
              readOnly={readOnly}
              options={siteDesignationOptions}
              direction="column"
              required
            />

            {formData.siteDesignations.includes("other") && (
              <OptimizedInput
                id="otherDesignation"
                label="Specify other designation"
                value={formData.otherDesignation}
                onChange={handleFieldChange("otherDesignation")}
                placeholder="Please specify the other site designation..."
                readOnly={readOnly}
              />
            )}
          </div>

          {/* Utility Company Section */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-medium text-slate-700 dark:text-foreground">
              Utility Company Requirements
            </h3>

            <div className="space-y-4">
              <OptimizedTextarea
                id="utilityCompanyConditions"
                label="Utility company conditions to be met"
                value={formData.utilityCompanyConditions}
                onChange={handleFieldChange("utilityCompanyConditions")}
                placeholder="List any technical or contractual conditions imposed by the utility company..."
                rows={3}
                readOnly={readOnly}
              />

              <OptimizedTextarea
                id="utilityCompanyRequirements"
                label="Utility company requirements to satisfy"
                value={formData.utilityCompanyRequirements}
                onChange={handleFieldChange("utilityCompanyRequirements")}
                placeholder="Describe any specific actions, documents, standards, or deliverables required by the utility company..."
                rows={3}
                readOnly={readOnly}
              />
            </div>
          </div>

          {/* Substation Delivery Section */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-medium text-slate-700 dark:text-foreground">
              Grid Connection Infrastructure
            </h3>

            <div className="space-y-3">
              <OptimizedSelect
                id="substationDeliveryBy"
                label="Who will deliver the substation/housing for the grid connection?"
                value={formData.substationDeliveryBy}
                onChange={handleSubstationDeliveryChange}
                placeholder="Select responsible party"
                options={substationDeliveryOptions}
                readOnly={readOnly}
              />

              {formData.substationDeliveryBy === "other" && (
                <OptimizedInput
                  id="substationDeliveryOther"
                  label="Specify other responsible party"
                  value={formData.otherDesignation}
                  onChange={handleFieldChange("otherDesignation")}
                  placeholder="Please specify who will deliver the substation..."
                  readOnly={readOnly}
                />
              )}
            </div>
          </div>
          <OptimizedTextarea
            id="planningAspectsRemarks"
            label="Other Remarks"
            value={formData.planningAspectsRemarks}
            onChange={handleFieldChange("planningAspectsRemarks")}
            placeholder="Enter any additional remarks regarding Planning Aspects"
            rows={3}
            readOnly={readOnly}
          />

          {/* Planning Aspects Images Section */}
          <ImageSection
            title="Planning Aspects Images"
            filterData={{ planning_aspects: true }}
            uploadFilterData={{ planning_aspects: true }}
            emptyMessage="Add images related to planning aspects"
            buttonText="Add Images"
          />
        </CardContent>
      </Card>

      {/* Planning Aspects Images Card */}
      {/* <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Planning Aspects Images
            </CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const input = document.createElement("input");
                input.type = "file";
                input.multiple = true;
                input.accept = "image/*";
                input.onchange = (e) => {
                  const target = e.target as HTMLInputElement;
                  handleImageUpload(target.files, { planning_aspects_main: true });
                };
                input.click();
              }}
              disabled={
                loading || isUploadingForSection({ planning_aspects_main: true })
              }
            >
              {isUploadingForSection({ planning_aspects_main: true }) ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              {isUploadingForSection({ planning_aspects_main: true })
                ? "Uploading..."
                : "Add Images"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isUploadingForSection({ planning_aspects_main: true }) && (
            <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50">
              <div className="text-center">
                <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2" />
                <p className="text-sm text-slate-600">
                  Uploading planning aspects images...
                </p>
              </div>
            </div>
          )}

          {!isUploadingForSection({ planning_aspects_main: true }) &&
          files?.filter(
            (file) =>
              file?.relatedTo?.entityType === "planning-aspects" &&
              file?.relatedTo?.entityId === currentSite?.id &&
              file?.relatedTo?.filterData?.planning_aspects_main
          ).length === 0 ? (
            <div className="flex items-center justify-between p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50">
              <div className="flex items-center gap-3">
                <Camera className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    No planning aspects images yet
                  </p>
                  <p className="text-xs text-slate-500">
                    Add images to document planning aspects
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {files
                ?.filter(
                  (file) =>
                    file?.relatedTo?.entityType === "planning-aspects" &&
                    file?.relatedTo?.entityId === currentSite?.id &&
                    file?.relatedTo?.filterData?.planning_aspects_main
                )
                .map((image) => (
                  imageDescription={imageDescriptions[image.id] ?? image?.fileDescription ?? ""}
                image={image}
                    onView={setSelectedImage}
                    onRemove={handleRemoveImage}
                    onDescriptionUpdate={updateImageDescription}
                    isDeleting={loading ? imageToDelete : false}
                  />
                ))}
            </div>
          )}
        </CardContent>
      </Card> */}

      {/* Delete Confirmation Modal */}
      {imageToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold">Delete Image</h3>
            </div>
            <p className="text-slate-600 dark:text-muted-foreground mb-6">
              Are you sure you want to delete this image? This action cannot be
              undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={cancelRemoveImage}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmRemoveImage}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-4xl max-h-full overflow-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedImage.originalName}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedImage(null)}
                disabled={loading}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4">
              <OfflineImage
                fileId={
                  selectedImage?._id ||
                  selectedImage?.id ||
                  selectedImage?._clientId
                }
                src={selectedImage?.fileUrl}
                alt={selectedImage.originalName}
                className="max-w-full max-h-96 mx-auto"
              />
              {selectedImage.fileDescription && (
                <div className="mt-4 p-3 bg-slate-50 dark:bg-muted rounded">
                  <p className="text-sm text-slate-700 dark:text-muted-foreground">
                    {selectedImage.fileDescription}
                  </p>
                </div>
              )}
              <div className="mt-2 text-sm text-slate-500 dark:text-muted-foreground">
                Size: {formatFileSize(selectedImage.fileSize)} • Date:{" "}
                {new Date(selectedImage.uploadDate).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress footer */}
      <div className="sticky bottom-2 md:bottom-4 z-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-white dark:bg-card rounded-lg border shadow-lg backdrop-blur-sm bg-white/95 dark:bg-surface-raised/95">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Form Status:
            </span>
            <Badge variant="default">Complete</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Progress:
            </span>
            <Badge variant="outline">
              {sessionProgress?.[templateId]?.["planning-aspects"] ||
                calculateProgress(formData)}
              %
            </Badge>
          </div>
        </div>
        <div className="text-xs md:text-sm text-slate-500 dark:text-muted-foreground hidden lg:block">
          Auto-save enabled • Changes saved automatically
        </div>
      </div>
    </div>
  );
});
